/**
 * Helpers for resource-scoped, short-lived signed download URLs.
 *
 * Why bother? The old pattern (`?auth=<JWT>` in a plain anchor) put the
 * user's full JWT into URL → browser history → referer header → proxy
 * logs. A leaked JWT = full account compromise until natural expiry.
 *
 * The signed URL only buys you 5 minutes and only reads one resource,
 * so even if a customer share leaks it into a screenshot, the blast
 * radius is small. See server.py `make_signed_url_params` for the
 * HMAC details.
 *
 * Use:
 *   import { signDownload, useSignedUrl } from "@/lib/signedUrl";
 *
 *   // Imperative (button click):
 *   const url = await signDownload("closeout_job", jobId);
 *   window.location.href = url;
 *
 *   // Declarative (img src):
 *   const src = useSignedUrl("file", fileId);
 *   return <img src={src} />;
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Module-level cache so a list of 30 photo thumbnails doesn't trigger
// 30 sign requests on every render. Keyed by `${kind}:${id}`. Each
// entry stores both the absolute URL and the expiry epoch so we can
// invalidate just before it would 401.
const _cache = new Map();
const _SAFETY_MARGIN_MS = 30_000; // refresh 30s before real expiry

/**
 * Request a signed URL for the given resource. Always returns an
 * absolute URL (includes REACT_APP_BACKEND_URL).
 */
export async function signDownload(kind, id) {
  const cacheKey = `${kind}:${id}`;
  const cached = _cache.get(cacheKey);
  if (cached && cached.expiresAt - Date.now() > _SAFETY_MARGIN_MS) {
    return cached.url;
  }
  const { data } = await api.post("/downloads/sign", { kind, id });
  const absolute = `${BACKEND_URL}${data.url}`;
  _cache.set(cacheKey, {
    url: absolute,
    expiresAt: data.expires_at * 1000,
  });
  return absolute;
}

/**
 * React hook: returns a signed URL for the given resource, or `null`
 * while the request is in flight. Refreshes automatically if the
 * component unmounts/remounts later when the cached URL has expired.
 */
export function useSignedUrl(kind, id) {
  const [url, setUrl] = useState(() => {
    const c = _cache.get(`${kind}:${id}`);
    if (c && c.expiresAt - Date.now() > _SAFETY_MARGIN_MS) return c.url;
    return null;
  });
  useEffect(() => {
    if (!id) { setUrl(null); return; }
    let alive = true;
    signDownload(kind, id)
      .then((u) => { if (alive) setUrl(u); })
      .catch(() => { if (alive) setUrl(null); });
    return () => { alive = false; };
  }, [kind, id]);
  return url;
}
